import { createFileRoute } from "@tanstack/react-router";
import { SeoPageShell, H2 } from "@/components/SeoPageShell";

const TITLE = "Like2Fit FAQ — AI Outfit Finder Questions Answered";
const DESC = "Answers to the most asked questions about Like2Fit — how the AI outfit finder works, retailers supported, pricing, privacy and more.";
const URL = "https://like2fit.com/faq";

const FAQS: { q: string; a: string }[] = [
  { q: "How does Like2Fit identify clothes from a photo?", a: "Like2Fit uses computer-vision AI trained on millions of fashion images. When you upload an outfit photo, the model detects each clothing item, reads its colour, material and silhouette, and matches it against live product catalogues from hundreds of retailers." },
  { q: "Is Like2Fit free?", a: "Yes — Like2Fit is free to use. You can upload outfit photos, get shoppable matches and build a wardrobe without a credit card." },
  { q: "Which retailers does Like2Fit search?", a: "We search hundreds of retailers in real time, from fast-fashion brands to premium designers and indie labels. You set the budget tier and we filter the results to match." },
  { q: "Can I shop the look from an Instagram or TikTok screenshot?", a: "Yes. Screenshots from Instagram, TikTok, Pinterest, street-style blogs or any other source all work — Like2Fit doesn't need a brand tag or product link to find the items." },
  { q: "Does Like2Fit work with clothes I already own?", a: "Yes. Style My Wardrobe lets you upload photos of clothes in your closet and the AI stylist app pairs them into outfits for any occasion. It also flags pieces missing to complete a look." },
  { q: "Will Like2Fit find the exact item or just similar ones?", a: "When the exact item is in stock and indexed, we surface it first. When it isn't — or it's out of your budget — we recommend the closest alternatives across budget, mid and premium tiers." },
  { q: "Is my photo data private?", a: "Yes. Uploaded photos are used only to find matches for you and are stored against your account. See our Privacy Policy for full details." },
  { q: "Do I need to sign up to try it?", a: "No. You can try the AI outfit finder without an account. Sign up only if you want to save outfits, build a wardrobe, or get daily styling suggestions." },
];

export const Route = createFileRoute("/faq")({
  component: FaqPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://like2fit.com/" },
            { "@type": "ListItem", position: 2, name: "FAQ", item: URL },
          ],
        }),
      },
    ],
  }),
});

function FaqPage() {
  return (
    <SeoPageShell
      eyebrow="FAQ"
      h1="Frequently asked questions"
      intro="Quick answers about how Like2Fit's AI outfit finder works, what it costs, and how your data is handled."
    >
      {FAQS.map((f) => (
        <div key={f.q}>
          <H2>{f.q}</H2>
          <p>{f.a}</p>
        </div>
      ))}
    </SeoPageShell>
  );
}
